import { useCallback, useState } from "react"
import { useAppToast } from "@ui-seeker/app"
import { postSubmit } from "@ui-seeker/utils"

import { BACKEND_APIS } from "@constants"
import {
    selectedBrandIds,
    isMergeModalOpen,
    targetBrandId,
    isMerging,
    tableData,
    mergeResult
} from "../@signals"


export default function useMerge() {
    const { showToast } = useAppToast()
    const [dryRun, setDryRun] = useState(false)

    const handleRowSelectionChange = useCallback((currentRowsSelected, allRowsSelected, rowsSelected) => {
        const selectedIds = rowsSelected.map(rowIndex => {
            const brand = tableData.value[rowIndex]
            return brand?.brandID
        }).filter(Boolean)
        selectedBrandIds.value = selectedIds
    }, [])

    const handleOpenMergeModal = useCallback(() => {
        if (selectedBrandIds.value.length < 1) {
            showToast.warning("Please select at least 1 brand to merge")
            return
        }
        isMergeModalOpen.value = true
    }, [showToast])

    const handleCloseMergeModal = useCallback(() => {
        isMergeModalOpen.value = false
        targetBrandId.value = null
        setDryRun(false)
    }, [])

    const handleDryRunChange = useCallback((event) => {
        setDryRun(event.target.checked)
    }, [])

    const handleTargetBrandChange = useCallback((event, option) => {
        targetBrandId.value = option?.value || null
    }, [])

    const getSelectedBrandNames = useCallback(() => {
        return selectedBrandIds.value.map(id => {
            const brand = tableData.value.find(b => b.brandID === id)
            return brand?.brandName || `Brand ${id}`
        })
    }, [])

    const executeMerge = useCallback(async (isDryRun) => {
        isMerging.value = true
        try {
            const result = await postSubmit(`${BACKEND_APIS.BRANDS}/crud/merge`, {
                sourceBrandIds: selectedBrandIds.value,
                targetBrandId: targetBrandId.value,
                dryRun: isDryRun
            })

            // Store result for the report modal
            mergeResult.value = result

            if (!isDryRun) {
                // Remove merged brands from table
                tableData.value = tableData.value.filter(
                    brand => !selectedBrandIds.value.includes(brand.brandID)
                )

                // Reset merge modal state
                selectedBrandIds.value = []
                isMergeModalOpen.value = false
                targetBrandId.value = null
                setDryRun(false)
            }
        } catch (error) {
            console.error("Failed to merge brands:", error)
            showToast.error(error.message || "Failed to merge brands")
        } finally {
            isMerging.value = false
        }
    }, [showToast])

    const handleCloseResultModal = useCallback(() => {
        mergeResult.value = null
    }, [])

    const handleMerge = useCallback(() => {
        if (!targetBrandId.value) {
            showToast.warning("Please select a target brand")
            return
        }

        if (dryRun) {
            // Execute dry run directly without confirmation
            executeMerge(true)
        } else {
            // Real merge - ask for confirmation
            const selectedNames = getSelectedBrandNames()
            const targetBrand = tableData.value.find(b => b.brandID === targetBrandId.value)
            const targetName = targetBrand?.brandName || `Brand ${targetBrandId.value}`

            showToast.confirm(
                `Are you sure you want to merge ${selectedNames.join(", ")} into ${targetName}? This action cannot be undone.`,
                { onSubmit: () => executeMerge(false) }
            )
        }
    }, [showToast, getSelectedBrandNames, dryRun, executeMerge])

    return {
        selectedCount: selectedBrandIds.value.length,
        isModalOpen: isMergeModalOpen.value,
        isMerging: isMerging.value,
        targetBrandId: targetBrandId.value,
        selectedBrandIds: selectedBrandIds.value,
        dryRun,
        mergeResult: mergeResult.value,

        handleRowSelectionChange,
        handleOpenMergeModal,
        handleCloseMergeModal,
        handleTargetBrandChange,
        handleDryRunChange,
        handleMerge,
        handleCloseResultModal,
        getSelectedBrandNames
    }
}
